import { Dialog, useTheme } from '@mui/material';

import { ATSConnectHiringRequestCandidateFeedbackTestIds } from 'data-testids/ATS';
import {
  CandidateFeedbackListModalPane,
  CandidateFeedbackListModalPaneValue,
  CandidateFeedbackModalPane,
  CandidateFeedbackModalProps,
} from '../types';
import { FormContainer } from './styles';
import { useState } from 'react';
import { CreatePane } from './Panes/Create/Scorecard/CreatePane';
import {
  ReferenceFeedbackListPane,
  ScorecardFeedbackListPane,
} from './Panes/List/FeedbackListPane';
import { FormProvider, useForm } from 'react-hook-form';
import { ScorecardToSubmit, ScorecardToTransform } from './Panes/Create/Scorecard/components/types';
import { useCreateHiringRequestScorecard } from './Panes/Create/Scorecard/mutations';
import { CTAButtons } from './Panes/Create/Scorecard/components/CTAButtons';
import { useQueryGetAvailableScorecardsByRoleData } from './Panes/Create/Scorecard/queries';
import { EditPane } from './Panes/Edit/EditPane';
import { useQueryGetScorecardsForHiringRequest } from './Panes/List/queries';
import { ErrorSnackbar, SuccessSnackbar } from 'sharedComponents/Snackbar';
import { getAxiosErrorMessageByStatusCode } from 'hooks/errorHandling/errorHandlingHooks';
import { useEditHiringRequestScorecard } from './mutations';
import { EditCTAButtons } from './Panes/Edit/components/EditCTAButtons';
import { ReferencePane } from './Panes/Create/Reference/ReferencePane';
import { CandidateFeedbackModalTypes } from '../enum';
import { HiringRequestToast, HiringRequestToastType } from './types';
import { FinalRecommendation } from 'utils/typedEnums';
import { ReadableFinalRecommendation } from '../types';
import auth from 'utils/auth';

export const CandidateFeedbackModal: React.FC<CandidateFeedbackModalProps> = ({
  candidateId,
  candidateName,
  currentModalOpen,
  hiringRequestId,
  open,
  onClose,
  onSuccess,
  roleTitle,
  roleId,
}) => {
  const [currentModalPane, setCurrentModalPane] = useState<CandidateFeedbackModalPane>({
    type:
      currentModalOpen.modalType === 'reference'
        ? CandidateFeedbackListModalPane.REFERENCE_LIST
        : CandidateFeedbackListModalPane.SCORECARD_LIST,
    scorecardId: null,
  });
  const [currentToast, setCurrentToast] = useState<{
    type: HiringRequestToastType;
    message: string;
  }>({
    type: null,
    message: null,
  });

  const { data: scorecardsAvailableForRole } = useQueryGetAvailableScorecardsByRoleData(roleId);
  const { data: completedScorecards } = useQueryGetScorecardsForHiringRequest(hiringRequestId, {
    enabled: currentModalOpen.modalType === CandidateFeedbackModalTypes.SCORECARD,
  });
  const theme = useTheme();
  const titleText =
    currentModalOpen?.modalType === CandidateFeedbackModalTypes.SCORECARD
      ? `Scorecards for ${candidateName}`
      : `References for ${candidateName}`;

  const createMethods = useForm<ScorecardToSubmit>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    criteriaMode: 'firstError',
  });
  const { watch } = createMethods;
  const districtId = auth.getUser()?.profile?.district.id;
  const {
    mutate: createScorecard,
    error: createError,
    isLoading: isCreateMutationLoading,
  } = useCreateHiringRequestScorecard(districtId, {
    onSuccess: (data) => {
      onSuccess(data.scorecard_complete);
    },
    onError: () => {
      setCurrentToast({ type: HiringRequestToast.ERROR, message: errorMessageFromCreating });
    },
  });
  const errorMessageFromCreating = getAxiosErrorMessageByStatusCode(createError);

  const editMethods = useForm({ defaultValues: { candidate_id: candidateId } });
  const {
    mutate: editScorecard,
    error: editError,
    isLoading: isEditMutationLoading,
  } = useEditHiringRequestScorecard(districtId, {
    onSuccess: (data) => {
      onSuccess(data.scorecard_complete);
    },
    onError: () => {
      setCurrentToast({ type: HiringRequestToast.ERROR, message: errorMessageFromEditing });
    },
  });
  const errorMessageFromEditing = getAxiosErrorMessageByStatusCode(editError);

  const id = watch('custom_scorecard_id');
  const selectedScorecard = scorecardsAvailableForRole?.find((scorecard) => scorecard.id === id);
  const completedScorecard = completedScorecards?.find(
    (scorecard) => scorecard.id === currentModalPane.scorecardId
  );

  const handleSetCurrentModalPane = (pane: CandidateFeedbackModalPane) => {
    setCurrentModalPane(pane);
  };

  const updatedOnClose = (type: CandidateFeedbackListModalPaneValue) => {
    setCurrentModalPane({ type });
    onClose();
  };

  const handleCancel = (type: CandidateFeedbackListModalPaneValue) => setCurrentModalPane({ type });

  const handleSubmit = (
    formState: ScorecardToTransform,
    isComplete: boolean,
    isEdit = false,
    scorecardId?: number
  ) => {
    const formData = new FormData();

    const processedAnswers = formState.answers.map((answer) => {
      if (answer.attachment instanceof File) {
        formData.append(answer.attachment.name, answer.attachment);
        return {
          ...answer,
          attachment: answer.attachment.name,
        };
      }

      return answer;
    });

    const finalRecommendationMap = {
      yes: FinalRecommendation.YES,
      maybe: FinalRecommendation.MAYBE,
      no: FinalRecommendation.NO,
    };

    let finalRecommendationValue: FinalRecommendation | ReadableFinalRecommendation =
      formState.final_recommendation;

    if (Object.keys(finalRecommendationMap).includes(formState.final_recommendation)) {
      finalRecommendationValue = finalRecommendationMap[formState.final_recommendation];
    }

    const payload = {
      ...formState,
      answers: processedAnswers,
      scorecard_complete: isEdit ? true : isComplete,
      final_recommendation: finalRecommendationValue,
    };

    formData.append('data', JSON.stringify(payload));

    if (isEdit) {
      editScorecard({
        hiringRequestId,
        scorecardId,
        payload: formData,
      });
    } else {
      createScorecard({
        hiringRequestId,
        payload: formData,
      });
    }
  };

  const handleSave = (formState: ScorecardToTransform, isComplete: boolean) => {
    handleSubmit(formState, isComplete, false, null);
  };

  const handleEdit = (formState: ScorecardToTransform) => {
    handleSubmit(formState, true, true, formState.completed_scorecard_id);
  };

  const handleEditScorecardClick = (completedScorecardId: number) => {
    handleSetCurrentModalPane({
      type: CandidateFeedbackListModalPane.EDIT,
      scorecardId: completedScorecardId,
    });
  };

  return (
    <Dialog
      data-testid={ATSConnectHiringRequestCandidateFeedbackTestIds.MODAL}
      maxWidth="lg"
      onClose={updatedOnClose}
      open={open}
      sx={{
        color: theme.palette.text.primary,
      }}
      PaperProps={{ style: { minWidth: '40%', borderRadius: theme.spacing(2) } }}
    >
      <FormContainer>
        {currentModalPane.type === CandidateFeedbackListModalPane.SCORECARD_LIST && (
          <FormProvider {...editMethods}>
            <ScorecardFeedbackListPane
              onClose={() => updatedOnClose(CandidateFeedbackListModalPane.SCORECARD_LIST)}
              roleTitle={roleTitle}
              titleText={titleText}
              onModalPaneChange={handleSetCurrentModalPane}
              modalType={currentModalOpen.modalType}
              hiringRequestId={hiringRequestId}
              onEditScorecardClick={handleEditScorecardClick}
            />
          </FormProvider>
        )}
        {currentModalPane.type === CandidateFeedbackListModalPane.REFERENCE_LIST && (
          <FormProvider {...editMethods}>
            <ReferenceFeedbackListPane
              onClose={() => updatedOnClose(CandidateFeedbackListModalPane.REFERENCE_LIST)}
              roleTitle={roleTitle}
              titleText={titleText}
              onModalPaneChange={handleSetCurrentModalPane}
              modalType={currentModalOpen.modalType}
              onEditScorecardClick={handleEditScorecardClick}
              candidateId={candidateId}
            />
          </FormProvider>
        )}
        {currentModalPane.type === CandidateFeedbackListModalPane.SCORECARD_CREATE && (
          <FormProvider {...createMethods}>
            <CreatePane
              candidateName={candidateName}
              modalType={currentModalOpen?.modalType}
              roleId={roleId}
            />
            <CTAButtons
              hasSelectedScorecard={Boolean(selectedScorecard)}
              onCancel={() => handleCancel(CandidateFeedbackListModalPane.SCORECARD_LIST)}
              onSaveAsDraft={(formState) => handleSave(formState, false)}
              onSaveAsCompleted={(formState) => handleSave(formState, true)}
              isLoading={isCreateMutationLoading}
            />
          </FormProvider>
        )}
        {currentModalPane.type === CandidateFeedbackListModalPane.REFERENCE_CREATE && (
          <ReferencePane
            onCancel={() => handleCancel(CandidateFeedbackListModalPane.REFERENCE_LIST)}
            candidateName={candidateName}
            candidateId={candidateId}
            hiringRequestId={hiringRequestId}
            onSuccess={(type: HiringRequestToastType, message: string) =>
              setCurrentToast({ type, message })
            }
          />
        )}
        {currentModalPane.type === CandidateFeedbackListModalPane.EDIT && (
          <FormProvider {...editMethods}>
            <EditPane
              candidateName={candidateName}
              created={completedScorecard.created}
              modalType={currentModalOpen?.modalType}
              reviewer={completedScorecard.submitted_by.name}
              templateName={completedScorecard.template.title}
            />
            <EditCTAButtons
              isLoading={isEditMutationLoading}
              onSave={(formState) => handleEdit(formState)}
              onCancel={() => handleCancel(CandidateFeedbackListModalPane.SCORECARD_LIST)}
            />
          </FormProvider>
        )}
      </FormContainer>

      <ErrorSnackbar
        dataTestId={ATSConnectHiringRequestCandidateFeedbackTestIds.ERROR_TOAST}
        open={currentToast?.type === HiringRequestToast.ERROR}
        message={errorMessageFromCreating || errorMessageFromEditing}
        onClose={() => setCurrentToast({ type: null, message: null })}
      />
      <SuccessSnackbar
        dataTestId={ATSConnectHiringRequestCandidateFeedbackTestIds.SUCCESS_TOAST}
        open={currentToast?.type === HiringRequestToast.SUCCESS}
        message={currentToast?.message}
        onClose={() => setCurrentToast(null)}
      />
    </Dialog>
  );
};
